'use client';

import { DateRange as DateRangeIcon } from '@mui/icons-material';
import {
  Box,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  type SxProps,
  styled,
} from '@mui/material';
import { type FocusEvent, type ReactNode, useEffect, useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';

import { MfDate, MfDateRange, Period } from '@campaign-manager/core/model';

import usePopover from '../../hooks/usePopover';
import Button from '../Button';
import { DateRangeValueString } from '../DateRangeValue';
import TextField from '../TextField';
import { calculateRelativeDateRange } from './utils';

const DatePickerWrapper = styled(Box)(
  ({ theme }) => `
& {
  .react-datepicker {
    border: none;
  }

  .react-datepicker__header {
    background: none;
    border-radius: unset;
    border-color: #e5e5e5;
  }

  .react-datepicker__navigation{
    width: 48px;
    height: 48px;
  }

  .react-datepicker__current-month{
    margin-top: 8px;
    margin-bottom: 12px;
  }

  .react-datepicker__day{
    font-size: 12px;
  }

  .react-datepicker__day--selected, .react-datepicker__day--in-range {
    background-color: ${theme.palette.secondary.main};
    font-size: 12px;
    font-weight: 500;
  }
},
`,
);

export type DateRangePickerValue = MfDateRange | undefined;

interface Props {
  locale?: string;

  name?: string;
  value?: DateRangePickerValue;
  required?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  label?: string;

  onChange?: (value: MfDateRange) => void;
  onBlur?: (e: FocusEvent<any>) => void;

  minDate?: MfDate;
  maxDate?: MfDate;

  disabled?: boolean;

  variant?: 'standard' | 'outlined' | 'filled';
  sx?: SxProps;
  popperSx?: SxProps;
  elevation?: number;

  showOnFieldClick?: boolean;
  showPresets?: boolean;
}

// #NOTE: Since we use it in a Next.js app, the react-datepicker styles can only be
// imported from the app's root layout (see apps/campaign-manager/app/layout.tsx)
export default function DateRangePicker({
  locale = 'en',
  name,
  value: initValue,
  required,
  error,
  helperText,
  label,
  onChange: onChangeCallback,
  onBlur,
  minDate,
  maxDate,
  disabled,
  variant = 'filled',
  sx,
  popperSx,
  showOnFieldClick,
  showPresets,
}: Props) {
  const { anchorEl, open, id, handleClick, handleKeyboardEvent, handleClose } = usePopover();

  const [startDate, setStartDate] = useState<MfDate | undefined>();
  const [endDate, setEndDate] = useState<MfDate | undefined>();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setStartDate(initValue?.start);
    setEndDate(initValue?.end);
  }, [initValue?.toISOString()]);

  const onChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;

    if (start) {
      setStartDate(
        MfDate.fromDateParts(start.getFullYear(), start.getMonth() + 1, start.getDate(), 0),
      );
    } else {
      setStartDate(undefined);
    }

    if (end) {
      setEndDate(MfDate.fromDateParts(end.getFullYear(), end.getMonth() + 1, end.getDate(), 0));
    } else {
      setEndDate(undefined);
    }
  };

  const handleRelativeRangeClick = (
    period: Period,
    count: number,
    relativeToPeriodStart?: boolean,
  ) => {
    let { start, end } = calculateRelativeDateRange(period, count, relativeToPeriodStart);

    // Clamp the range to minDate/maxDate constraints
    if (minDate && start.before(minDate)) {
      start = minDate;
    }
    if (maxDate && end.after(maxDate)) {
      end = maxDate;
    }

    setStartDate(start);
    setEndDate(end);
  };

  const handleCancelClick = () => {
    handleClose();
  };

  const handleApplyClick = () => {
    if (!startDate || !endDate) {
      return;
    }

    handleClose();
    onChangeCallback?.(new MfDateRange(startDate, endDate));
  };

  const pickerStartDate = useMemo(
    () => (startDate ? new Date(startDate.year, startDate.month - 1, startDate.day) : undefined),
    [startDate?.toISOString()],
  );
  const pickerEndDate = useMemo(
    () => (endDate ? new Date(endDate.year, endDate.month - 1, endDate.day) : undefined),
    [endDate?.toISOString()],
  );

  const pickerMinDate = useMemo(
    () => (minDate ? new Date(minDate.year, minDate.month - 1, minDate.day) : undefined),
    [minDate?.toISOString()],
  );
  const pickerMaxDate = useMemo(
    () => (maxDate ? new Date(maxDate.year, maxDate.month - 1, maxDate.day) : undefined),
    [maxDate?.toISOString()],
  );

  return (
    <Box
      sx={{
        position: 'relative',
      }}
    >
      <TextField
        variant={variant}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <IconButton
                  onClick={handleClick}
                  disabled={disabled}
                  edge="start"
                  aria-label="Open calendar"
                >
                  <DateRangeIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        name={name}
        onKeyDown={(e) => {
          if (e.key === 'Tab') {
            return;
          }
          handleKeyboardEvent(e);
        }}
        {...(showOnFieldClick ? { onClick: handleClick } : {})}
        value={DateRangeValueString({ start: startDate, end: endDate }) ?? ''}
        onBlur={onBlur}
        required={required}
        disabled={disabled}
        error={error}
        helperText={helperText}
        label={label}
        sx={{ ...sx, minWidth: 232 }}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
      />
      <ClickAwayListener
        mouseEvent="onMouseDown"
        touchEvent="onTouchStart"
        onClickAway={handleClose}
      >
        <Popper
          id={id}
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: 1301, ...popperSx }} // z-index is on top of modals
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
          ]}
        >
          <Paper
            sx={{
              height: 378,
              borderRadius: 'var(--paperBorderRadius-default)',
            }}
            elevation={3}
          >
            <Stack
              direction="row"
              sx={{ height: '100%', overflow: 'hidden' }}
            >
              {showPresets && (
                <Box sx={{ overflow: 'auto' }}>
                  <MenuList>
                    <MenuItem
                      sx={{ px: 4 }}
                      onClick={() => handleRelativeRangeClick(Period.DAY, -7)}
                    >
                      Last 7 days
                    </MenuItem>
                    <MenuItem
                      sx={{ px: 4 }}
                      onClick={() => handleRelativeRangeClick(Period.DAY, -30)}
                    >
                      Last 30 days
                    </MenuItem>
                    <MenuItem
                      sx={{ px: 4 }}
                      onClick={() => handleRelativeRangeClick(Period.DAY, -90)}
                    >
                      Last 90 days
                    </MenuItem>
                    <MenuItem
                      sx={{ px: 4 }}
                      onClick={() => handleRelativeRangeClick(Period.MONTH, -1, true)}
                    >
                      Last month
                    </MenuItem>
                    <MenuItem
                      sx={{ px: 4 }}
                      onClick={() => handleRelativeRangeClick(Period.MONTH, -12)}
                    >
                      Last 12 months
                    </MenuItem>
                    <MenuItem
                      sx={{ px: 4 }}
                      onClick={() => handleRelativeRangeClick(Period.YEAR, -1, true)}
                    >
                      Last year
                    </MenuItem>
                    <MenuItem
                      sx={{ px: 4 }}
                      onClick={() => handleRelativeRangeClick(Period.WEEK, 0)}
                    >
                      Week to date
                    </MenuItem>
                    <MenuItem
                      sx={{ px: 4 }}
                      onClick={() => handleRelativeRangeClick(Period.MONTH, 0)}
                    >
                      Month to date
                    </MenuItem>
                    <MenuItem
                      sx={{ px: 4 }}
                      onClick={() => handleRelativeRangeClick(Period.QUARTER, 0)}
                    >
                      Quarter to date
                    </MenuItem>
                    <MenuItem
                      sx={{ px: 4 }}
                      onClick={() => handleRelativeRangeClick(Period.YEAR, 0)}
                    >
                      Year to date
                    </MenuItem>
                  </MenuList>
                </Box>
              )}
              <Stack
                sx={{
                  justifyContent: 'space-between',
                  flex: 1,
                }}
              >
                <DatePickerWrapper>
                  <ReactDatePicker
                    locale={locale}
                    onChange={onChange}
                    selected={pickerStartDate}
                    startDate={pickerStartDate}
                    endDate={pickerEndDate}
                    selectsRange
                    inline
                    monthsShown={2}
                    {...(minDate && { minDate: pickerMinDate })}
                    {...(maxDate && { maxDate: pickerMaxDate })}
                    disabled={disabled}
                  />
                </DatePickerWrapper>
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: 'flex-end',
                    gap: 1,
                    pt: 3,
                    pb: 3,
                    px: 3,
                  }}
                >
                  <Button
                    onClick={handleCancelClick}
                    color="inherit"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleApplyClick}
                    disabled={!startDate || !endDate}
                  >
                    Apply
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Paper>
        </Popper>
      </ClickAwayListener>
    </Box>
  );
}
